import React, { useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ActualStayDateTimeEditDialog from './ActualStayDateTimeEditDialog';

const DEFAULT_ACCENT = '#4ce1b6';

export default function EditableActualStayField({
  label,
  display,
  momentValue,
  scheduledHint,
  canEdit = false,
  kind = 'checkin',
  isDark = true,
  accent = DEFAULT_ACCENT,
  onSave,
  saving = false,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const openEditor = () => {
    if (!canEdit) return;
    setDialogOpen(true);
  };

  const squareChipSx = {
    height: 'auto',
    minHeight: 40,
    borderRadius: 1,
    maxWidth: '100%',
    backgroundColor: alpha(accent, isDark ? 0.14 : 0.1),
    color: accent,
    border: `1px solid ${alpha(accent, 0.32)}`,
    '& .MuiChip-label': {
      px: 1.25,
      py: 0.75,
      width: '100%',
      display: 'block',
    },
    ...(canEdit && {
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: `0 6px 16px ${alpha(accent, 0.25)}`,
        backgroundColor: alpha(accent, isDark ? 0.2 : 0.14),
      },
    }),
  };

  const chipContent = (dateText, timeText) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        width: '100%',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {dateText ? (
          <Typography
            component="span"
            variant="caption"
            sx={{ display: 'block', fontWeight: 700, lineHeight: 1.35, fontSize: '0.72rem' }}
          >
            {dateText}
          </Typography>
        ) : null}
        {timeText ? (
          <Typography
            component="span"
            variant="caption"
            sx={{ display: 'block', fontWeight: 700, lineHeight: 1.35, fontSize: '0.72rem' }}
          >
            {timeText}
          </Typography>
        ) : null}
      </Box>
      {canEdit ? <EditIcon sx={{ fontSize: 15, flexShrink: 0, color: accent }} /> : null}
    </Box>
  );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <CalendarTodayIcon sx={{ fontSize: 18, color: accent, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ color: isDark ? alpha('#fff', 0.55) : '#666', display: 'block', width: '100%', mb: 0.5 }}
          >
            {label}
          </Typography>
          {display?.date || display?.time ? (
            <Chip
              clickable={canEdit}
              onClick={openEditor}
              label={chipContent(display?.date, display?.time)}
              size="small"
              sx={squareChipSx}
            />
          ) : canEdit ? (
            <Chip
              clickable
              onClick={openEditor}
              label={chipContent('Set date & time', null)}
              size="small"
              variant="outlined"
              sx={{
                ...squareChipSx,
                borderColor: alpha(accent, 0.45),
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? alpha('#fff', 0.35) : '#999' }}>
              —
            </Typography>
          )}
        </Box>
      </Box>

      <ActualStayDateTimeEditDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={label}
        subtitle="Update the recorded date and time in hotel timezone."
        scheduledHint={scheduledHint}
        value={momentValue}
        onSave={async (next) => {
          await onSave?.(next);
          setDialogOpen(false);
        }}
        saving={saving}
        isDark={isDark}
        accent={accent}
        kind={kind}
      />
    </>
  );
}
